;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import type { Breadcrumb, ErrorEvent } from "@sentry/nextjs";

// Substring match: provider schemas use `apiToken`, `personalAccessToken`,
// `credentials`. Bare `key`/`auth` would hide ids such as `queryKeyRoot`.
const SENSITIVE_KEY_PATTERN =
	/token|secret|password|passwd|credential|cookie|authorization|auth[-_]?header|api[-_]?key|private[-_]?key/i;

// Too short for a substring match: `code` would hide `statusCode`.
// `context.` is the prefix `toLogAttributes` adds before `beforeSendLog` runs.
const SENSITIVE_EXACT_KEY_PATTERN =
	/^(?:context\.)?(?:code|otp|otp[-_]?code|verification[-_]?code)$/i;

// LLM usage counts, not credentials.
const TOKEN_COUNT_KEY_PATTERN =
	/^(?:context\.)?(?:(?:input|output|total|prompt|completion|cached|reasoning|max)[-_]?tokens|tokens?[-_]?(?:count|usage|used|budget|limit))$/i;

const SENSITIVE_QUERY_PARAM_PATTERN =
	/((?:^|[?&])(?:code|token|access_token|id_token|refresh_token|client_secret|state|api_key|apikey|password|secret|signature|sig)=)[^&#\s]+/gi;

// `postgres://user:pass@host`, `https://user:pass@host`. The lookbehind starts
// matches only at a token start: without it a long token-like string with no
// `://` is rescanned from every position (quadratic).
const URL_CREDENTIALS_PATTERN =
	/(?<![\w+.-])([a-z][\w+.-]*:\/\/)[^\s/:@]+:[^\s/@]+@/gi;

// Keeps the first character and the domain so support can still tell users
// apart. Same token-start lookbehind as above, to stay linear.
const EMAIL_PATTERN =
	/(?<![\w.%+-])([\w.%+-])[\w.%+-]*@([\w-]+(?:\.[\w-]+)*\.[a-z]{2,})\b/gi;

// Library error messages embed raw ID tokens (google-auth-library does).
const JWT_PATTERN = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g;

const BEARER_TOKEN_PATTERN = /(\bBearer\s+)[\w.~+/=-]+/gi;

const NETWORK_FAILURE_MESSAGE_FRAGMENTS = [
	"failed to fetch",
	"networkerror",
	"network request failed",
	"load failed",
] as const;

const BROWSER_NOISE_MESSAGE_FRAGMENTS = [
	"resizeobserver loop",
	"non-error promise rejection captured",
] as const;

const THIRD_PARTY_SCRIPT_LOCATION_FRAGMENTS = [
	"chatbase.co",
	"smartlook.com",
	"cdn-cgi/rum",
	"chrome-extension://",
	"moz-extension://",
	"safari-extension://",
] as const;

const SCRUB_MAX_DEPTH = 8;
// Log attributes are cut to 512 characters, so walking more is wasted work.
const SCRUB_MAX_ENTRIES = 50;

const SENSITIVE_HEADER_DENYLIST = [
	"forwarded",
	"-ip",
	"remote-",
	"via",
	"-user",
];

// Also applies to spans and logs, which `beforeSend` never sees.
const SENSITIVE_QUERY_PARAM_DENYLIST = [
	...SENSITIVE_HEADER_DENYLIST,
	"code",
	"state",
	"token",
	"client_secret",
	"id_token",
	"access_token",
	"refresh_token",
];

/**
 * SDK 11 collects cookies, bodies, headers, user info, DB query data, queue
 * payloads and stack-frame local variables by default. Tenant identity is set
 * explicitly (`setUser({ id })`, tenant attributes) instead.
 */
export const SENTRY_DATA_COLLECTION = {
	cookies: false,
	databaseQueryData: false,
	genAI: { inputs: false, outputs: false },
	graphQL: { document: false, variables: false },
	httpBodies: [],
	httpHeaders: {
		request: { deny: SENSITIVE_HEADER_DENYLIST },
		response: { deny: SENSITIVE_HEADER_DENYLIST },
	},
	queues: false,
	// Filtering by name doesn't work after minification, so collect none.
	stackFrameVariables: false,
	urlQueryParams: { deny: SENSITIVE_QUERY_PARAM_DENYLIST },
	userInfo: false,
};

/** Page text is shown on purpose (see spec). Secret inputs stay masked. */
export const SENTRY_REPLAY_OPTIONS = {
	blockAllMedia: true,
	mask: [
		"input[type='password']",
		"input[type='email']",
		"input[name*='token' i]",
		"input[name*='secret' i]",
		"input[name*='apiKey' i]",
		"input[name*='api-key' i]",
		"input[autocomplete='current-password']",
		"input[autocomplete='new-password']",
	],
	maskAllText: false,
};

function isSensitiveKey(key: string): boolean {
	if (TOKEN_COUNT_KEY_PATTERN.test(key)) {
		return false;
	}
	return (
		SENSITIVE_KEY_PATTERN.test(key) || SENSITIVE_EXACT_KEY_PATTERN.test(key)
	);
}

export function scrubSensitiveString(text: string): string {
	return text
		.replace(SENSITIVE_QUERY_PARAM_PATTERN, "$1[Filtered]")
		.replace(URL_CREDENTIALS_PATTERN, "$1[Filtered]@")
		.replace(JWT_PATTERN, "[Filtered]")
		.replace(BEARER_TOKEN_PATTERN, "$1[Filtered]")
		.replace(EMAIL_PATTERN, "$1***@$2");
}

function summarizeOpaqueObject(value: object): string | undefined {
	if (value instanceof ArrayBuffer) {
		return `[Binary ${value.byteLength} bytes]`;
	}
	if (ArrayBuffer.isView(value)) {
		return `[Binary ${value.byteLength} bytes]`;
	}
	if (value instanceof Map) {
		return `[Map size=${value.size}]`;
	}
	if (value instanceof Set) {
		return `[Set size=${value.size}]`;
	}
	return undefined;
}

function scrubValue({
	ancestors,
	depth,
	value,
}: {
	ancestors: Set<object>;
	depth: number;
	value: unknown;
}): unknown {
	if (typeof value === "string") {
		return scrubSensitiveString(value);
	}
	if (!value || typeof value !== "object" || value instanceof Date) {
		return value;
	}
	if (value instanceof Error) {
		return scrubSensitiveString(`${value.name}: ${value.message}`);
	}
	const opaqueSummary = summarizeOpaqueObject(value);
	if (opaqueSummary) {
		return opaqueSummary;
	}
	// `ancestors` holds only the current path, so shared (non-cyclic) refs survive.
	if (ancestors.has(value) || depth >= SCRUB_MAX_DEPTH) {
		return "[Truncated]";
	}
	ancestors.add(value);

	let scrubbed: unknown;
	if (Array.isArray(value)) {
		const scrubbedItems: unknown[] = value
			.slice(0, SCRUB_MAX_ENTRIES)
			.map((item) => scrubValue({ ancestors, depth: depth + 1, value: item }));
		if (value.length > SCRUB_MAX_ENTRIES) {
			scrubbedItems.push(`[${value.length - SCRUB_MAX_ENTRIES} more]`);
		}
		scrubbed = scrubbedItems;
	} else {
		const keys = Object.keys(value);
		const scrubbedEntries: Record<string, unknown> = {};
		for (const key of keys.slice(0, SCRUB_MAX_ENTRIES)) {
			const nestedValue = (value as Record<string, unknown>)[key];
			scrubbedEntries[key] = isSensitiveKey(key)
				? "[Filtered]"
				: scrubValue({ ancestors, depth: depth + 1, value: nestedValue });
		}
		if (keys.length > SCRUB_MAX_ENTRIES) {
			scrubbedEntries["[Truncated]"] =
				`${keys.length - SCRUB_MAX_ENTRIES} more keys`;
		}
		scrubbed = scrubbedEntries;
	}

	ancestors.delete(value);
	return scrubbed;
}

export function scrubSensitiveValue(value: unknown): unknown {
	return scrubValue({ ancestors: new Set(), depth: 0, value });
}

/** The console integration puts the whole printed line in `message`. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
	return {
		...breadcrumb,
		...(breadcrumb.message && {
			message: scrubSensitiveString(breadcrumb.message),
		}),
		...(breadcrumb.data && {
			data: scrubSensitiveValue(breadcrumb.data) as Breadcrumb["data"],
		}),
	};
}

export type IgnoredExceptionReason = "cancelled" | "network" | "browser-noise";

function getIgnoredMessageReason(
	errorMessage: string,
): IgnoredExceptionReason | null {
	const normalizedMessage = errorMessage.toLowerCase();
	const includesAny = (fragments: readonly string[]) =>
		fragments.some((fragment) => normalizedMessage.includes(fragment));

	if (includesAny(NETWORK_FAILURE_MESSAGE_FRAGMENTS)) {
		return "network";
	}
	if (includesAny(BROWSER_NOISE_MESSAGE_FRAGMENTS)) {
		return "browser-noise";
	}
	return null;
}

export function getIgnoredExceptionReason(
	error: unknown,
): IgnoredExceptionReason | null {
	if (typeof error === "string") {
		return getIgnoredMessageReason(error);
	}
	if (!error || typeof error !== "object") {
		return null;
	}

	const errorName =
		"name" in error && typeof error.name === "string" ? error.name : "";
	if (errorName === "CancelledError" || errorName === "AbortError") {
		return "cancelled";
	}

	const errorMessage =
		"message" in error && typeof error.message === "string"
			? error.message
			: "";
	return getIgnoredMessageReason(errorMessage);
}

/**
 * Only when every frame is a vendor script or extension: extensions that wrap
 * `fetch` also appear on the stack of real app errors.
 */
export function isThirdPartyInstallationError(event: ErrorEvent): boolean {
	const frames = (event.exception?.values ?? []).flatMap(
		(exceptionValue) => exceptionValue.stacktrace?.frames ?? [],
	);
	if (frames.length === 0) {
		return false;
	}
	return frames.every((frame) => {
		const location =
			`${frame.filename ?? ""} ${frame.abs_path ?? ""}`.toLowerCase();
		return THIRD_PARTY_SCRIPT_LOCATION_FRAGMENTS.some((fragment) =>
			location.includes(fragment),
		);
	});
}
